import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import BeneficiaryDetailsContent from '@UI/Pages/Beneficiaries/BeneficiaryDetails/BeneficiaryDetailsContent';
import BeneficiariesConfirmationDialogs from '@UI/Pages/Beneficiaries/BeneficiaryDetails/ConfirmationDialogs/BeneficiariesConfirmationDialogs';
import BeneficiariesNotifications from '@UI/Pages/Beneficiaries/Shared/BeneficiariesNotifications';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import DocumentsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentsNotifications';
import InterviewsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewsNotifications';
import UpsertBeneficiarySidebar from '@UI/Pages/Beneficiaries/Sidebars/UpsertBeneficiarySidebar';
import SubjectScreenContainer from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/SubjectScreenContainer';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';

/**
 * Assembles the screen: breadcrumb, the data-owning `BeneficiaryDetailsContent`, the sidebar,
 * confirmation dialogs and notifications. Redirects to the beneficiary list when the route's `beneficiary`
 * segment is not a valid id, and only mounts `BeneficiaryDetailsContent` once it is — the content never
 * has to consider an invalid id itself.
 */
const BeneficiaryDetails: React.FC = () => {
	const navigate = useNavigate();
	const { subject } = useSubjectAddressParams();

	React.useEffect(() => {
		if (subject === null) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
		}
	}, [subject, navigate]);

	return (
		<SubjectScreenContainer>
			<ConditionalLoader data={subject}>
				{(validSubject) => (
					<React.Fragment>
						<SubjectBreadcrumb subject={validSubject} />
						<BeneficiaryDetailsContent subject={validSubject} />
					</React.Fragment>
				)}
			</ConditionalLoader>

			<UpsertBeneficiarySidebar />
			<BeneficiariesConfirmationDialogs />
			<BeneficiariesNotifications />
			<InterviewsNotifications />
			<DocumentsNotifications />
		</SubjectScreenContainer>
	);
};

export default BeneficiaryDetails;
